/**
 * Placeholder titles for notes created without one.
 *
 * A note submitted with the title field blank still needs a title: the column
 * is NOT NULL and the create handler rejects "". Rather than force a title out
 * of the user at the moment they least want to think of one, the UI stamps a
 * placeholder here and the sync daemon's titling pass replaces it later with
 * something a local model derived from the body.
 *
 * The `untitled-` prefix is load-bearing — it is exactly what
 * GET /api/notes/untitled matches on (store.UntitledPrefix). Changing the shape
 * of this string without changing that query means notes silently never get
 * titled. The rest of it is only there to be unique, legible and sortable, so
 * that a note whose titling never runs is merely ugly rather than ambiguous.
 */

const PREFIX = "untitled";

/** Lowercase, hyphen-separated, ASCII-safe. Folders may contain "/". */
function slugify(value: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return slug === "" ? "note" : slug;
}

function pad(value: number): string {
  return String(value).padStart(2, "0");
}

/**
 * Builds `untitled-<folder>-YYYY-MM-DD-HH-MM-SS` in local time.
 *
 * Local rather than UTC because the timestamp is only ever read by a human
 * scanning a note list, and a note they wrote at 9pm should not be labelled
 * with tomorrow's date.
 */
export function placeholderTitle(folder: string, now: Date = new Date()): string {
  const stamp = [
    now.getFullYear(),
    pad(now.getMonth() + 1),
    pad(now.getDate()),
    pad(now.getHours()),
    pad(now.getMinutes()),
    pad(now.getSeconds()),
  ].join("-");

  return `${PREFIX}-${slugify(folder)}-${stamp}`;
}

/** True when a title is one this module generated (or the server is waiting to replace). */
export function isPlaceholderTitle(title: string): boolean {
  return title.startsWith(`${PREFIX}-`);
}
